/**
 * Mortgage Calculator Types
 *
 * Type definitions for mortgage calculation features.
 */

// Loan term options in years
export const LOAN_TERM_OPTIONS = [1, 5, 10, 15, 20, 25, 30] as const;
export type LoanTerm = typeof LOAN_TERM_OPTIONS[number];

/**
 * A custom monthly expense the user wants to factor into the breakdown.
 * Useful for subscription-style expenses (Netflix, car payment, daycare, …).
 */
export interface OtherExpense {
    /** Stable id for React keys / list mutations. */
    id: string;
    label: string;
    /** Monthly USD amount. */
    amount: number;
}

/**
 * Input values for mortgage calculation
 */
export interface MortgageInputs {
    purchasePrice: number;
    downPayment: number;
    downPaymentPercentage: number;
    loanTerm: LoanTerm;
    interestRate: number;
    annualPropertyTax: number;
    pmiPercentage: number;
    annualHomeInsurance: number;
    monthlyHOA: number;
    /** Custom monthly expenses (subscriptions, car payment, etc.). */
    otherExpenses: OtherExpense[];
}

/**
 * Breakdown of monthly mortgage payment components
 */
export interface MortgageBreakdown {
    principalAndInterest: number;
    taxes: number;
    mortgageInsurance: number;
    others: number;
    total: number;
    loanAmount: number;
}

/**
 * Single row in amortization schedule
 */
export interface AmortizationRow {
    month: number;
    date: Date;
    principal: number;
    interest: number;
    taxes: number;
    pmi: number;
    totalPayment: number;
    balance: number;
}

/**
 * Amortization schedule grouped by year
 */
export interface AmortizationYear {
    year: string;
    rows: AmortizationRow[];
    yearlyPrincipal: number;
    yearlyInterest: number;
    yearlyTotal: number;
}

/**
 * Summary of amortization schedule
 */
export interface AmortizationSummary {
    totalPayments: number;
    totalInterest: number;
    totalPrincipal: number;
    years: AmortizationYear[];
}

/** Cap how many custom expense rows we let users add. */
export const MAX_OTHER_EXPENSES = 20;

/**
 * Default values for mortgage calculator
 */
export const DEFAULT_MORTGAGE_INPUTS: MortgageInputs = {
    purchasePrice: 500000,
    downPayment: 100000,
    downPaymentPercentage: 20,
    loanTerm: 30,
    interestRate: 5.83,
    annualPropertyTax: 5000,
    pmiPercentage: 0.5,
    annualHomeInsurance: 0,
    monthlyHOA: 0,
    otherExpenses: [],
};

/**
 * Chart colors for mortgage breakdown
 */
export const MORTGAGE_CHART_COLORS = {
    principalAndInterest: '#D4A853', // Gold
    taxes: '#9CA3AF',               // Gray
    mortgageInsurance: '#B8860B',   // Dark Gold
    others: '#6B7280',              // Dark Gray
} as const;

/**
 * Labels for mortgage breakdown
 */
export const MORTGAGE_BREAKDOWN_LABELS = {
    principalAndInterest: 'Principal & Interest',
    taxes: 'Taxes',
    mortgageInsurance: 'Mortgage Insurance',
    others: 'Others',
} as const;
